import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService, type AuthEmployeeProfile, type EffectivePrivilege } from '../services/api';

interface AuthContextType {
  authUser: string | null | undefined;
  employee: AuthEmployeeProfile | null | undefined;
  effectivePrivilege: EffectivePrivilege | null | undefined;
  authError: string;
  isAdmin: boolean;
  checkAuth: () => Promise<void>;
  handleLogin: (employeeNumber: string, password: string) => Promise<void>;
  handleLogout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authUser, setAuthUser] = useState<string | null | undefined>(undefined);
  const [employee, setEmployee] = useState<AuthEmployeeProfile | null | undefined>(undefined);
  const [effectivePrivilege, setEffectivePrivilege] = useState<EffectivePrivilege | null | undefined>(undefined);
  const [authError, setAuthError] = useState<string>('');

  const isAdmin = effectivePrivilege?.actions?.includes('administer_ugp') === true;

  const checkAuth = useCallback(async () => {
    try {
      const timeoutPromise = new Promise<null>((_, reject) =>
        setTimeout(() => reject(new Error('Auth check timed out')), 20000)
      );
      const me = await Promise.race([apiService.me(), timeoutPromise]);
      if (me && (me as any).user) {
        setAuthUser((me as any).user);
        setEmployee((me as any).employee ?? null);
        setEffectivePrivilege((me as any).effectivePrivilege ?? null);
      } else {
        setAuthUser(null);
        setEmployee(null);
        setEffectivePrivilege(null);
      }
    } catch {
      setAuthUser(null);
      setEmployee(null);
      setEffectivePrivilege(null);
    }
  }, []);

  useEffect(() => {
    setAuthUser(undefined);
    setEmployee(undefined);
    setEffectivePrivilege(undefined);
    checkAuth();
  }, [checkAuth]);

  const handleLogin = useCallback(async (employeeNumber: string, password: string) => {
    setAuthError('');
    try {
      const me = await apiService.me();
      if (me && me.mode === 'disabled') {
        setAuthUser(employeeNumber || 'anonymous');
        setEmployee(me.employee ?? null);
        setEffectivePrivilege(me.effectivePrivilege ?? null);
        return;
      }
      const loginResp = await apiService.loginSimple(employeeNumber, password);
      setEffectivePrivilege(loginResp?.effectivePrivilege ?? null);
      // Prefer the employee profile returned at login; fall back to /auth/me
      // if the login response didn't carry it (older backend / HR unavailable).
      if (loginResp?.employee) {
        setEmployee(loginResp.employee);
      } else {
        const meAfterLogin = await apiService.me();
        if (!meAfterLogin) {
          setAuthError('Login failed - backend may not be responding');
          setAuthUser(null);
          setEmployee(null);
          setEffectivePrivilege(null);
          return;
        }
        setEmployee(meAfterLogin.employee ?? null);
        setEffectivePrivilege(meAfterLogin.effectivePrivilege ?? null);
      }
      setAuthUser(loginResp?.user || employeeNumber);
    } catch (err: any) {
      let errorMessage = 'Login failed. Please try again.';
      if (err?.response?.status === 401) {
        errorMessage = 'Invalid employee number or password. Password changes daily.';
      } else if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setAuthError(errorMessage);
      setAuthUser(null);
      setEmployee(null);
      setEffectivePrivilege(null);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try { await apiService.logout(); } catch {}
    setAuthUser(null);
    setEmployee(null);
    setEffectivePrivilege(null);
  }, []);

  return (
    <AuthContext.Provider value={{ authUser, employee, effectivePrivilege, authError, isAdmin, checkAuth, handleLogin, handleLogout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
